<template>
  <DropdownMenu :location="location" activator="click">
    <template #activator>
      <button type="button" class="ShareButton" @click.stop>
        <Icon icon="$shareVariant" />
      </button>
    </template>
    <MenuList>
      <MenuItem @click="shareOnWhatsapp">
        <template #prepend>
          <Icon icon="$whatsapp" />
        </template>
        <span class="ml-4">{{ $t('share.shareWhatsapp') }}</span>
      </MenuItem>
      <MenuItem @click="shareOnFacebook">
        <template #prepend>
          <Icon icon="$facebook" />
        </template>
        <span class="ml-4">{{ $t('share.shareFb') }}</span>
      </MenuItem>
      <MenuItem @click="shareByEmail">
        <template #prepend>
          <Icon icon="$emailMultipleOutline" />
        </template>
        <span class="ml-4">{{ $t('share.shareEmail') }}</span>
      </MenuItem>
      <MenuItem @click="shareWithLink">
        <template #prepend>
          <Icon icon="$contentCopy" />
        </template>
        <span class="ml-4">{{ $t('share.copyLink') }}</span>
      </MenuItem>
    </MenuList>
  </DropdownMenu>
</template>

<script setup lang="ts">
import DropdownMenu from '@/components/ui/DropdownMenu.vue'
import MenuList from '@/components/ui/MenuList.vue'
import MenuItem from '@/components/ui/MenuItem.vue'
import Icon from '@/components/ui/Icon.vue'
import { NotificationType } from '@/models/enums/app/NotificationType'
import { addNotification } from '@/services/notifications/NotificationService'
import { i18n } from '@/plugins/i18n'
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    additionnalPath: string
    externalLink?: boolean
    location?: 'bottom' | 'top' | 'left' | 'right'
  }>(),
  {
    externalLink: false,
    location: 'right',
  },
)

const url = computed(() => {
  return props.externalLink ? props.additionnalPath : window.location.href + props.additionnalPath
})

const body = computed(() => {
  return i18n.t('share.genericBody') + ' ' + url.value
})

async function shareWithLink() {
  await navigator.clipboard.writeText(url.value)
  addNotification(i18n.t('share.urlCopied'), NotificationType.SUCCESS)
}

function shareByEmail() {
  const mailto = `mailto:?subject=${encodeURIComponent(i18n.t('share.mailSubject'))}&body=${encodeURIComponent(body.value)}`
  window.location.href = mailto
}

function shareOnWhatsapp() {
  const whatsappLink = `https://wa.me/?text=${url.value}`
  window.open(whatsappLink, '_blank')
}

function shareOnFacebook() {
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url.value}`
  window.open(facebookShareUrl, '_blank', 'width=600,height=400')
}
</script>

<style scoped>
.ShareButton {
  background: transparent;
  border: none;
  color: rgb(var(--v-theme-main-blue));
  cursor: pointer;
  padding: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
}

.ShareButton:hover {
  opacity: 0.8;
}
</style>
